import { z } from 'zod';
import { matryoshkaSchema } from './matryoshka.js';
import { marketingGoalSchema } from './marketing-goal.js';

/**
 * Single question in an idea questionnaire.
 * `title` — bold heading shown to the user.
 * `description` — optional smaller-font hint/example below the heading.
 */
export const ideaQuestionSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(2000).nullable().default(null),
});

export type IdeaQuestion = z.infer<typeof ideaQuestionSchema>;

/**
 * URL accepted by admin endpoints — either an absolute URL (https://...)
 * or a server-relative path that starts with `/uploads/` (returned by
 * `POST /admin/ideas/upload-{video,thumbnail}`, which writes into the
 * shared `uploads` volume and serves it from nginx).
 */
const mediaUrlSchema = z.string().refine(
  (s) => /^https?:\/\//i.test(s) || s.startsWith('/uploads/'),
  { message: 'must be https://… or /uploads/…' },
);

/**
 * Complete idea schema with all fields including system_prompt.
 * Used primarily for admin/backend operations and detail views.
 */
export const ideaSchema = z.object({
  id: z.number().int().positive(),
  formatId: z.number().int().positive(),
  code: z.string(),
  title: z.string(),
  description: z.string(),
  audienceAction: z.string().nullable(),
  matryoshka: matryoshkaSchema,
  thumbnailUrl: z.string(),
  previewVideoUrls: z.array(z.string()),
  systemPrompt: z.string(),
  questions: z.array(ideaQuestionSchema),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Idea = z.infer<typeof ideaSchema>;

/**
 * Public DTO for idea lists in TMA: without system_prompt.
 * Returned by list endpoints.
 */
export const ideaPublicSchema = ideaSchema.pick({
  id: true,
  formatId: true,
  code: true,
  title: true,
  description: true,
  audienceAction: true,
  matryoshka: true,
  thumbnailUrl: true,
  previewVideoUrls: true,
  questions: true,
  sortOrder: true,
});

export type IdeaPublic = z.infer<typeof ideaPublicSchema>;

/**
 * Detail DTO for a single idea view in TMA.
 * Includes public fields + marketing_goals array.
 * Does NOT include system_prompt (stays hidden from client).
 */
export const ideaDetailSchema = ideaPublicSchema.extend({
  marketingGoals: z.array(marketingGoalSchema),
});

export type IdeaDetail = z.infer<typeof ideaDetailSchema>;

/**
 * DTO for creating an idea (admin API).
 * All required fields except audienceAction (nullable).
 */
export const ideaAdminCreateSchema = z.object({
  formatId: z.number().int().positive(),
  code: z.string().min(1).max(64),
  title: z.string().min(1).max(256),
  description: z.string().min(1),
  audienceAction: z.string().max(500).nullable().optional(),
  matryoshka: matryoshkaSchema,
  thumbnailUrl: mediaUrlSchema,
  previewVideoUrls: z.array(mediaUrlSchema).default([]),
  systemPrompt: z.string().min(1),
  questions: z.array(ideaQuestionSchema).default([]),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().nonnegative().default(0),
  marketingGoalIds: z.array(z.number().int().positive()).default([]),
});

export type IdeaAdminCreate = z.infer<typeof ideaAdminCreateSchema>;

/**
 * DTO for updating an idea (admin API).
 * All fields are optional for PATCH requests.
 */
export const ideaAdminUpdateSchema = z.object({
  formatId: z.number().int().positive().optional(),
  code: z.string().min(1).max(64).optional(),
  title: z.string().min(1).max(256).optional(),
  description: z.string().min(1).optional(),
  audienceAction: z.string().max(500).nullable().optional(),
  matryoshka: matryoshkaSchema.optional(),
  thumbnailUrl: mediaUrlSchema.optional(),
  previewVideoUrls: z.array(mediaUrlSchema).optional(),
  systemPrompt: z.string().min(1).optional(),
  questions: z.array(ideaQuestionSchema).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  marketingGoalIds: z.array(z.number().int().positive()).optional(),
});

export type IdeaAdminUpdate = z.infer<typeof ideaAdminUpdateSchema>;
